import React, { useMemo, useState, useCallback } from "react";
import {
  App as AntApp,
  Card,
  Typography,
  Button,
  Tooltip,
  Flex,
  Modal,
} from "antd";
import {
  MessageOutlined,
  EditOutlined,
  DeleteOutlined,
} from "@ant-design/icons";

import { BasicModal } from "../../../Shared";
import { AssistanceForm } from "../AssistanceForm";
import { useAuth } from "../../../../hooks";
import { AssistanceApi } from "../../../../api";
import { AssistantEducaChat } from "../../../AssistantEduca";
import { Assistant_createthread } from "../../../../api/AssistantEducaAPI";
import { AssistantCard } from "../../../../ui";
import { NEW_ASISTENTES } from "../../../../ui/featureFlags";
import { useTranslation } from "react-i18next";

import styles from "./AssistanceItem.module.scss";

const { Text, Paragraph } = Typography;

function AssistanceItemInner(props) {
  const { assistance, onReload, canEdit } = props;
  const { accessToken } = useAuth();
  const { message, modal } = AntApp.useApp();
  const { t } = useTranslation("assistants");

  const assistanceController = useMemo(() => new AssistanceApi(), []);

  const [showEdit, setShowEdit] = useState(false);
  const [showChat, setShowChat] = useState(false);
  const [threadId, setThreadId] = useState(null);
  const [chatLoading, setChatLoading] = useState(false);

  const onOpenCloseEdit = () => setShowEdit((prev) => !prev);

  const openCloseChat = useCallback(async () => {
    if (!showChat && !threadId) {
      try {
        setChatLoading(true);
        const newThreadId = await Assistant_createthread(
          accessToken,
          assistance._id,
        );
        setThreadId(newThreadId);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error("Error al crear el thread:", error);
        message.error(t("item.chatError"));
        setChatLoading(false);
        return;
      } finally {
        setChatLoading(false);
      }
    }
    setShowChat((prev) => !prev);
  }, [accessToken, assistance._id, message, showChat, threadId, t]);

  const confirmDelete = useCallback(() => {
    modal.confirm({
      title: t("item.deleteTitle"),
      content: t("item.deleteContent", { name: assistance.title }),
      okText: t("item.deleteConfirm"),
      cancelText: t("item.deleteCancel"),
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          await assistanceController.deleteAssistance(
            accessToken,
            assistance._id,
          );
          if (typeof onReload === "function") onReload();
          message.success(t("item.deleteSuccess"));
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error(error);
          message.error(t("item.deleteError"));
        }
      },
    });
  }, [
    accessToken,
    assistance._id,
    assistance.title,
    assistanceController,
    message,
    modal,
    onReload,
    t,
  ]);

  return (
    <>
      {NEW_ASISTENTES ? (
        <AssistantCard
          name={assistance.title}
          avatar={assistance.miniature}
          ownerLabel={canEdit ? t("item.mineBadge") : t("item.officialBadge")}
          ownerVariant={canEdit ? "mine" : "official"}
          chatLabel={t("item.chatButton")}
          onChat={openCloseChat}
          chatLoading={chatLoading}
          canEdit={canEdit}
          onEdit={onOpenCloseEdit}
          onDelete={confirmDelete}
          editLabel={t("item.edit")}
          deleteLabel={t("item.delete")}
        />
      ) : (
        <Card
          className={styles.card}
          variant="outlined"
          styles={{ body: { padding: 16 } }}
        >
          <Flex gap={16} align="stretch" className={styles.body} wrap>
            <div className={styles.info}>
              <Text className={styles.title} title={assistance.title}>
                {assistance.title}
              </Text>

              <Paragraph
                className={styles.path}
                ellipsis={{ rows: 2, tooltip: assistance.path }}
              >
                {assistance.path}
              </Paragraph>

              <Flex gap={10} align="center" className={styles.actions} wrap>
                <Tooltip title={t("item.openChat")}>
                  <Button
                    aria-label={t("item.openChat")}
                    icon={<MessageOutlined />}
                    loading={chatLoading}
                    onClick={openCloseChat}
                    className={styles.btnChat}
                  />
                </Tooltip>

                {canEdit ? (
                  <>
                    <Tooltip title={t("item.edit")}>
                      <Button
                        aria-label={t("item.edit")}
                        icon={<EditOutlined />}
                        onClick={onOpenCloseEdit}
                        className={styles.btnEdit}
                      />
                    </Tooltip>

                    <Tooltip title={t("item.delete")}>
                      <Button
                        aria-label={t("item.delete")}
                        icon={<DeleteOutlined />}
                        onClick={confirmDelete}
                        className={styles.btnDelete}
                      />
                    </Tooltip>
                  </>
                ) : null}
              </Flex>
            </div>

            <div className={styles.cover}>
              <img
                src={assistance.miniature}
                alt={assistance.title}
                className={styles.image}
              />
            </div>
          </Flex>
        </Card>
      )}

      {/* Edit */}
      <BasicModal
        show={showEdit}
        close={onOpenCloseEdit}
        title={t("item.editTitle")}
        size="small"
      >
        <AssistanceForm
          assistance={assistance}
          onClose={onOpenCloseEdit}
          onReload={onReload}
        />
      </BasicModal>

      {/* Chat */}
      <Modal
        open={showChat}
        onCancel={openCloseChat}
        footer={null}
        width={720}
        className={`chatbot-modal ${styles.chatModal}`}
        styles={{ body: { padding: 0 } }}
        destroyOnHidden
      >
        <div className={styles.chatHero}>
          <div className={styles.heroAvatar}>
            {assistance.miniature ? (
              <img src={assistance.miniature} alt={assistance.title} />
            ) : (
              <MessageOutlined />
            )}
          </div>
          <div className={styles.heroText}>
            <div className={styles.heroName}>{assistance.title}</div>
            <div className={styles.heroRole}>
              <span className={styles.heroDot} />
              {t("item.chatRole")}
            </div>
            <div className={styles.heroHint}>{t("item.chatHint")}</div>
          </div>
        </div>
        <div className={styles.chatBody}>
          <AssistantEducaChat
            assistantId={assistance._id}
            avatarImg={assistance.miniature}
            saludo={assistance.title}
            threadId={threadId}
          />
        </div>
      </Modal>
    </>
  );
}

export function AssistanceItem(props) {
  return (
    <AntApp>
      <AssistanceItemInner {...props} />
    </AntApp>
  );
}
